import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useMemo } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { userContext } from "../../stores/users/userContext";
import { ROUTES } from "../../constants/routes";
import { takePostLoginIntent } from "../../constants/postLoginIntent";
import Logout from "../logout/logout";
import SiteHome from "../pages/homepage/siteLayout/SiteHome";
import SimplePage from "../pages/homepage/simple/SimplePage";
import CreateVideo from "../grid/createVideo";

import SharedCreateVideo from "../grid/SharedCreateVideo";
import ArchiveView from "../grid/ArchiveView";
import ReelPage from "../reel/ReelPage";
import StudioQueuePage from "../studio/StudioQueuePage";
import NotFound from "../pages/not_found_404/NotFound404";
import SubscriptionPlans from "../subscription/SubscriptionPlans";
import SubscriptionSuccess from "../subscription/SubscriptionSuccess";
import SubscriptionCancel from "../subscription/SubscriptionCancel";
import SubscriptionManagement from "../subscription/SubscriptionManagement";
import LandingPage from "../pages/landing/LandingPage";
import MyLibraryContainer from "../pages/myLibrary/MyLibraryContainer";
// Public marketing pages (viewable while logged OUT): R = flagship home, S = Premiere plugin.
import FlagshipPage from "../pages/homepage/components/FlagshipPage";
import PluginPage from "../pages/homepage/components/PluginPage";
// Home layouts W / X / Y (October 2026), public so they can be shared for feedback.
import LayoutWPage from "../pages/homepage/components/home3/LayoutWPage";
import LayoutXPage from "../pages/homepage/components/home3/LayoutXPage";
import LayoutYPage from "../pages/homepage/components/home3/LayoutYPage";
import LayoutZPage from "../pages/homepage/components/home3/LayoutZPage";
import GetAppPage from "../app/GetAppPage";
import PluginDownloadPage from "../plugin/PluginDownloadPage";
import ContactPage from "../contact/ContactPage";
import AppDownloadPage from "../app/AppDownloadPage";
import LegalPage from "../legal/LegalPage";
import ConnectorGuidePage from "../connector/ConnectorGuidePage";
import DeleteAccountPage from "../account/DeleteAccountPage";
// The phone app: the SAME bundle inside the Capacitor shell (/mobile) boots straight into the
// phone flows; on the web they live under /m for phone-browser testing (see mobile/native.js).
import MobileApp from "../mobile/MobileApp";
import { isNativeApp } from "../mobile/native";
import { isPublicPage } from "./publicPages";

// /create-video/?t=<transcriptId> — a deep link into the editor. The ET reads its transcript
// from the user store (the library sets it before navigating), so links from outside the app
// (the MCP connector's replies, a bookmark) need this wrapper to seed the store first. Synchronous
// on purpose: CreateVideo reads the id once on its first render.
const CreateVideoRoute = () => {
  const userStore = useContext(userContext);
  const { search } = useLocation();
  const wanted = parseInt(new URLSearchParams(search).get("t") || "", 10);
  if (Number.isFinite(wanted) && wanted > 0 && userStore.getUserTranscriptId() !== wanted) {
    userStore.setUserTranscript(null, "", wanted, 0);
  }
  return <CreateVideo />;
};

const SimpleRoute = () => {
  const q = new URLSearchParams(useLocation().search);
  const skin = q.get("skin");
  const headline = q.get("h");
  return (
    <SimplePage
      skin={skin === "red" || skin === "dark" ? skin : "cream"}
      headline={headline === "is" || headline === "cut" || headline === "cherries" ? headline : "becomes"}
    />
  );
};

const Router = () => {
  const userStore = useContext(userContext);
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = userStore.isUserAuth();
  const isStripeActive = userStore.isUserStripeActive();

  // Routing configuration table
  const routeConfig = useMemo(
    () => ({
      [ROUTES.HOME]: {
        // The site's address shows whichever home page the owner made live (SiteHome), to
        // everyone. It used to be auth-only and bounce signed-out visitors to /flagship, which
        // painted the "Oops" 404 page for a moment first on every fresh visit. (2026-10-24)
        component: SiteHome,
        authRequired: false,
        need_stripe_check: false,
      },
      [ROUTES.LOGOUT]: {
        component: Logout,
        authRequired: false,
        redirectIfAuth: false,
        need_stripe_check: false,
      },
      [ROUTES.LANDING]: {
        component: LandingPage,
        authRequired: false,
        redirectIfAuth: ROUTES.HOME,
        need_stripe_check: false,
      },
      [ROUTES.SUBSCRIPTION]: { component: SubscriptionPlans, authRequired: true, redirectIfNotAuth: ROUTES.LANDING, need_stripe_check: false },
      [ROUTES.SUBSCRIPTION_SUCCESS]: { component: SubscriptionSuccess, authRequired: true, redirectIfNotAuth: ROUTES.LANDING, need_stripe_check: false },
      [ROUTES.SUBSCRIPTION_CANCEL]: { component: SubscriptionCancel, authRequired: false, redirectIfNotAuth: ROUTES.LANDING, need_stripe_check: false },
      [ROUTES.SUBSCRIPTION_MANAGE]: { component: SubscriptionManagement, authRequired: true, redirectIfNotAuth: ROUTES.LANDING, need_stripe_check: false },
      // Public plans page. The Premiere panel links here when someone hits a plan limit,
      // so it must NOT require auth — bouncing them to the landing page at the exact moment
      // they decided to pay is how you lose the sale.
      [ROUTES.PRICING]: { component: SubscriptionPlans, authRequired: false, need_stripe_check: false },
      // Support — several pages already linked to /contact/ and it 404'd.
      [ROUTES.CONTACT]: { component: ContactPage, authRequired: false, need_stripe_check: false },
      // Panel download. Public page (so people can read what they need), but the .zxp itself
      // is served only to a signed-in account by the backend.
      [ROUTES.PLUGIN_DOWNLOAD]: { component: PluginDownloadPage, authRequired: false, need_stripe_check: false },
      [ROUTES.CREATE_VIDEO]: {
        component: CreateVideoRoute,
        authRequired: true,
        redirectIfNotAuth: ROUTES.LANDING,
        need_stripe_check: true,
      },
      // Archive — several uploads as ONE sheet (the real ET in archive mode). /archive/?ids=6,9
      [ROUTES.REEL]: {
        component: ReelPage,
        authRequired: true,
        redirectIfNotAuth: ROUTES.LANDING,
        need_stripe_check: false,
      },
      [ROUTES.STUDIO]: {
        component: StudioQueuePage,
        authRequired: true,
        redirectIfNotAuth: ROUTES.LANDING,
        need_stripe_check: false,
      },
      [ROUTES.ARCHIVE]: {
        component: ArchiveView,
        authRequired: true,
        redirectIfNotAuth: ROUTES.LANDING,
        need_stripe_check: true,
      },
      [ROUTES.MY_LIBRARY]: {
        component: MyLibraryContainer,
        authRequired: true,
        redirectIfNotAuth: ROUTES.LANDING,
        need_stripe_check: true,
      },
      [ROUTES.MY_LIBRARY_UPLOADS]: {
        component: MyLibraryContainer,
        authRequired: true,
        redirectIfNotAuth: ROUTES.LANDING,
        need_stripe_check: true,
      },
      [ROUTES.MY_LIBRARY_EXPORTS]: {
        component: MyLibraryContainer,
        authRequired: true,
        redirectIfNotAuth: ROUTES.LANDING,
        need_stripe_check: true,
      },
      [ROUTES.MY_LIBRARY_CALENDAR]: {
        component: MyLibraryContainer,
        authRequired: true,
        redirectIfNotAuth: ROUTES.LANDING,
        need_stripe_check: true,
      },
      [ROUTES.MY_LIBRARY_MEETINGS]: {
        component: MyLibraryContainer,
        authRequired: true,
        redirectIfNotAuth: ROUTES.LANDING,
        need_stripe_check: true,
      },
      [ROUTES.MY_LIBRARY_ARTISTIC]: {
        component: MyLibraryContainer,
        authRequired: true,
        redirectIfNotAuth: ROUTES.LANDING,
        need_stripe_check: true,
      },
      [ROUTES.MY_LIBRARY_SEARCH]: {
        component: MyLibraryContainer,
        authRequired: true,
        redirectIfNotAuth: ROUTES.LANDING,
        need_stripe_check: true,
      },
      [ROUTES.NOT_FOUND]: {
        component: NotFound,
        authRequired: false,
        redirectIfAuth: false,
        need_stripe_check: false,
      },
    }),
    [],
  );

  useEffect(() => {
    // Inside the phone app there is no auth wall and no 404 table — the shell IS the app.
    if (isNativeApp()) return;
    // Public pages (the share links, marketing, legal, /ai, /m) skip the auth redirect + 404
    // logic below. The list lives in publicPages.js: add a new public page there too.
    if (isPublicPage(location.pathname)) {
      return;
    }
    // Get the current path configuration
    const currentPath = location.pathname.endsWith("/")
      ? location.pathname
      : `${location.pathname}/`;
    // If path doesn't exist in our config, show 404
    if (!routeConfig[currentPath]) {
      navigate(ROUTES.NOT_FOUND);
      return;
    }
    const routeData = routeConfig[currentPath];
    // Handle authentication-based redirects
    if (routeData) {
      if (isAuthenticated && routeData.redirectIfAuth) {
        // Someone who clicked Subscribe while logged out is being bounced through login.
        // Send them where they were going, not to the homepage.
        const intent = takePostLoginIntent();
        if (intent) {
          navigate(intent);
          return;
        }
        if (routeData.redirectIfAuth === "stripe_check") {
          // Handle Stripe status check for authenticated users
          if (routeData.need_stripe_check) {
            navigate(isStripeActive ? ROUTES.HOME : ROUTES.LANDING);
          } else {
            navigate(ROUTES.HOME);
          }
        } else if (typeof routeData.redirectIfAuth === "string") {
          navigate(routeData.redirectIfAuth);
        } else {
          navigate(ROUTES.HOME);
        }
      } else if (!isAuthenticated && routeData.authRequired) {
        const redirectPath = routeData.redirectIfNotAuth || ROUTES.LANDING;
        navigate(redirectPath);
      }
    }
  }, [location, isAuthenticated, isStripeActive, routeConfig, navigate]);

  // Generate routes based on authentication status
  // A signed-out visitor on an auth-only page is redirected by the effect above, AFTER the first
  // paint. Leaving the route out made that first paint fall through to the "*" route, so they saw
  // the "Oops" 404 page flash before the redirect. Keep the route and render nothing instead.
  const routes = Object.entries(routeConfig).map(([path, config]) => (
    <Route
      key={path}
      exact
      path={path}
      element={!config.authRequired || isAuthenticated ? <config.component /> : null}
    />
  ));

  if (isNativeApp()) {
    return (
      <Routes>
        <Route path="/*" element={<MobileApp />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {routes}
      {/* Public shared-ET page — outside the auth gate */}
      <Route path="/s/:token" element={<SharedCreateVideo />} />
      {/* Public marketing pages — viewable logged-out (share these for feedback) */}
      <Route path="/flagship" element={<FlagshipPage />} />
      {/* The upload-first "Simple" home page on its own link, whatever is live at "/". ?skin=red|dark &h=is|cut|cherries */}
      <Route path="/simple" element={<SimpleRoute />} />
      <Route path="/plugin" element={<PluginPage />} />
      <Route path="/w" element={<LayoutWPage />} />
      <Route path="/x" element={<LayoutXPage />} />
      <Route path="/y" element={<LayoutYPage />} />
      <Route path="/z" element={<LayoutZPage />} />
      {/* The QR code / store link: an iPhone goes to the App Store, an Android phone to Google Play */}
      <Route path="/get" element={<GetAppPage />} />
      {/* The Android app: what it does, the download, the install taps */}
      <Route path="/app" element={<AppDownloadPage />} />
      {/* Legal. Public and login-free — Play checks the privacy URL from a signed-out browser */}
      <Route path="/privacy" element={<LegalPage doc="privacy" />} />
      <Route path="/terms" element={<LegalPage doc="terms" />} />
      {/* How to use CherryClipz from Claude / ChatGPT — the AI app directories' docs link. Public. */}
      <Route path="/ai" element={<ConnectorGuidePage />} />
      {/* Both stores require this, and Google wants the URL in the Data safety form. Public on
          purpose: somebody who cannot sign in is exactly who arrives here. */}
      <Route path="/delete-account" element={<DeleteAccountPage />} />
      {/* The phone app's flows on the web — test them in a phone browser at /m */}
      <Route path="/m/*" element={<MobileApp />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default observer(Router);
